import React, { useState } from 'react';
import '../../App.css';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// functions
import { APIPrefix } from '../../utils/functions';

// images
import saveIcon from '../../icons/save.128.png';
import refreshIcon from '../../icons/refresh.512.png';
import infoIcon from '../../icons/info.512.png';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import { HideDropdownMenu } from '../../utils/functions';
import SearchResultsTable from './search-results-table';
import SearchResultsFooter from '../../tools/search-results/search-results-footer';
import { useTranslation } from 'react-i18next';
import SaveQueryModal from './save-query-modal';
import Scrollbox from '../../tools/scrollbox';
import MiniButton from '../../tools/mini-button';

// types

// event-type imports.
import { Event, EventType, EventTarget, ExecuteADQLQuery } from '../../utils/events';

// types specifically for this component.
import { SelectedRowsType, LocationsType, QueryParameters } from './search-catalog-types';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  setDPTFilter,
  setInfoExpanded,
  setPage,
  setPageSize,
} from './search-catalog-slice';
import { buildQueryDescription, getTapData } from './search-catalog-actions';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SearchCatalogResults(props: {
  key: string;
  query: QueryParameters;
  hidden: boolean;
  launchService: any;
  tabID: string;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL STATE VARIABLES:
  // ------------------------------

  const sG_SessionValid: boolean | undefined = useAppSelector(
    (state) => state.global.sessionValid,
  );
  const sG_Project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_DarkMode: boolean = useAppSelector((state) => state.global.darkMode);

  // LOCAL STATE VARIABLES:
  // ------------------------------

  // LOCAL STATE VARIABLES FOR ALL TABS:
  // ------------------------------

  // handle paging.
  const sTotalAllTabs: { id: string; total: { dpt: string; total: number }[] }[] =
    useAppSelector((state) => state.searchCatalog.total);
  const sPagesAllTabs: { id: string; pages: number }[] = useAppSelector(
    (state) => state.searchCatalog.pages,
  );
  const sPageAllTabs: { id: string; page: number }[] = useAppSelector(
    (state) => state.searchCatalog.page,
  );
  // maintain the state for the selected rows on the results tabs.
  const sPageSizeAllTabs: { id: string; pageSize: number }[] = useAppSelector(
    (state) => state.searchCatalog.pageSize,
  );
  const sDPTFilterAllTabs: { id: string; dptFilter: string }[] = useAppSelector(
    (state) => state.searchCatalog.dptFilter,
  );
  const sDPTInResultsAllTabs: { id: string; dpt: string[] }[] = useAppSelector(
    (state) => state.searchCatalog.dptInResults,
  );
  const sDPTRetrievedAllTabs: { id: string; dptRetrieved: boolean }[] = useAppSelector(
    (state) => state.searchCatalog.dptRetrieved,
  );

  // the info tab and query
  const sInfoExpandedAllTabs: { id: string; infoExpanded: boolean }[] = useAppSelector(
    (state) => state.searchCatalog.infoExpanded,
  );
  const sBuiltQueryAllTabs: { id: string; builtQuery: string | undefined }[] =
    useAppSelector((state) => state.searchCatalog.builtQuery);

  // EXTRACT LOCAL STATE VARIABLES:
  // ------------------------------

  // manage data-product types.
  const sDPTFilter: string | undefined =
    sDPTFilterAllTabs.find((item) => item.id === props.tabID)?.dptFilter ?? undefined;
  const sDPTInResults: string[] | undefined =
    sDPTInResultsAllTabs.find((item) => item.id === props.tabID)?.dpt ?? undefined;
  const sDPTRetrieved: boolean | undefined =
    sDPTRetrievedAllTabs.find((item) => item.id === props.tabID)?.dptRetrieved ??
    undefined;

  // manage paging.
  const sTotalAllDPTs: { dpt: string; total: number }[] | undefined =
    sTotalAllTabs.find((item) => item.id === props.tabID)?.total ?? undefined;
  const sTotal: number =
    sTotalAllDPTs?.find((item) => item.dpt === sDPTFilter)?.total ?? 0;
  const sPages: number | undefined =
    sPagesAllTabs.find((item) => item.id === props.tabID)?.pages ?? undefined;
  const sPage: number | undefined =
    sPageAllTabs.find((item) => item.id === props.tabID)?.page ?? undefined;
  const sPageSize: number | undefined =
    sPageSizeAllTabs.find((item) => item.id === props.tabID)?.pageSize ?? undefined;

  // the info tab and query.
  const sInfoExpanded: boolean | undefined =
    sInfoExpandedAllTabs.find((item) => item.id === props.tabID)?.infoExpanded ??
    undefined;
  const sBuiltQuery: string | undefined =
    sBuiltQueryAllTabs.find((item) => item.id === props.tabID)?.builtQuery ?? undefined;

  //	-------------------------------------------------
  //
  //	state variables
  //
  //	-------------------------------------------------

  const { t, i18n } = useTranslation();

  // the modal form for saving queries.
  const [sShowModal, setShowModal] = useState(false);

  // hold the state of the info panel at the top of the component.
  const [sInfoHovered, setInfoHovered] = useState<boolean>(false);

  //	-------------------------------------------------
  //
  //	handler for button onClick events.
  //
  //	-------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    event.preventDefault();

    const button: HTMLButtonElement = event.currentTarget;

    console.log('button.name:');
    console.log(button.name);

    // save the current query.
    if (button.id === 'save-query-button') {
      setShowModal(true);
    }
    // re-run the current query.
    if (button.id === 'refresh-query-button') {
      dispatch(getTapData({ query: props.query }));
    }

    // open the current query in the ADQL editor.
    if (button.name === 'adql-editor') {
      //
      event.stopPropagation();

      if (sBuiltQuery !== undefined) {
        //
        const params: ExecuteADQLQuery = {
          adqlQuery: sBuiltQuery,
          displayOnly: true,
        };

        const newEvent: Event = {
          eventTarget: EventTarget.SEARCH_CATALOG_FORM,
          eventType: EventType.EXECUTE_ADQL_QUERY,
          parameters: params,
        };

        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
      //
    }
    //
  }; // onClickBtnHandler

  //	-------------------------------------------------
  //
  //	handler for div onClick events.
  //
  //	-------------------------------------------------

  const onClickDivHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const div: HTMLDivElement = event.currentTarget;

    // if the user selects an item on the short description, then stop the propagation so that the panel doesn't expand.
    if (div.id === 'short-query-description' || div.id === 'long-query-description') {
      const selection: Selection | null = window.getSelection();
      if (selection?.toString() ?? '' !== '') {
        event.stopPropagation();
      }
    }
    //
  }; // onClickDivHandler

  //	-------------------------------------------------
  //
  //	function that handles a change in the desired page size
  //
  //	-------------------------------------------------

  const pageSizeOnChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    var newPageSize: number = sPageSize ?? 10;
    if (Number(event.target.value)) {
      newPageSize = parseInt(event.target.value);
    }

    // update state with the selected value.
    dispatch(setPageSize({ id: props.tabID, pageSize: newPageSize }));
    //
  }; // pageSizeOnChangeHandler

  //	------------------------------------------------------------
  //
  //	Handles an update page event from the results footer.
  //
  //	------------------------------------------------------------

  const updatePage = function (args: { page: number }) {
    //
    dispatch(setPage({ id: props.tabID, page: args.page }));
    //
  }; // updatePage

  //	--------------------------------------------------------------------------
  //
  //	H O O K S
  //
  //	--------------------------------------------------------------------------

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  return (
    <div
      key={props.query.id}
      className="search-results-tab"
      data-hidden={props.hidden === true ? 'T' : 'F'}
    >
      <div style={{ flex: '0 0 2px' }} />

      {/*
       //  non-expanded query information panel
       */}
      <div
        className="text-colour"
        style={{
          flex: '0 0 36px',
          margin: '0px 10px 0px 10px',
          display: sInfoExpanded ? 'none' : 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          border:
            '1pt solid ' +
            (sInfoHovered
              ? sG_DarkMode
                ? 'white'
                : 'black'
              : sG_DarkMode
                ? '#555'
                : '#999'),
          borderRadius: '6px',
          cursor: 'pointer',
          position: 'relative',
        }}
        onMouseEnter={() => setInfoHovered(true)}
        onMouseLeave={() => setInfoHovered(false)}
        onClick={() => dispatch(setInfoExpanded({ id: props.tabID, infoExpanded: true }))}
      >
        <div style={{ flex: '0 0 5px' }} />
        <img src={infoIcon} alt="Information" width="24" height="24" />
        <div style={{ flex: '0 0 8px' }} />
        <div
          id="short-query-description"
          style={{
            flex: '1 0',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '36px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
          onClick={onClickDivHandler}
        >
          {buildQueryDescription({
            query: props.query,
            builtQuery: sBuiltQuery,
          })}
        </div>
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 16px',
            display: 'flex',
            flexDirection: 'row',
            position: 'absolute',
            bottom: '-8px',
            width: '100%',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              border:
                '1pt solid ' +
                (sInfoHovered
                  ? sG_DarkMode
                    ? 'white'
                    : 'black'
                  : sG_DarkMode
                    ? '#555'
                    : '#999'),
              flex: '0 0 20px',
              height: '15px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '5pt',
              backgroundColor: '#040034',
              color: 'white',
              fontWeight: '800',
            }}
          >
            ⋁
          </div>
          <div style={{ flex: '1 1' }} />
        </div>
      </div>
      <div style={{ flex: '0 0 8px', display: sInfoExpanded ? 'none' : 'flex' }} />

      {/*
       //  expanded query information panel
       */}
      <div
        className="text-colour"
        style={{
          flex: '1 1',
          margin: '0px 10px 0px 10px',
          display: sInfoExpanded ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'center',
          border:
            '1pt solid ' +
            (sInfoHovered
              ? sG_DarkMode
                ? 'white'
                : 'black'
              : sG_DarkMode
                ? '#555'
                : '#999'),
          borderRadius: '6px',
          cursor: 'pointer',
          position: 'relative',
        }}
        onMouseEnter={() => setInfoHovered(true)}
        onMouseLeave={() => setInfoHovered(false)}
        onClick={() =>
          dispatch(setInfoExpanded({ id: props.tabID, infoExpanded: false }))
        }
      >
        {/* the column containing filter boxes, etc. */}
        <Scrollbox direction="vertical" width="100%" flex="1 1" flexDirection="column">
          <div style={{ flex: '0 0 5px' }} />
          <div
            style={{ flex: '1 1', display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <div style={{ flex: '0 0 5px' }} />
            <img src={infoIcon} alt="Information" width="24" height="24" />
            <div style={{ flex: '0 0 8px' }} />
            <div
              id="long-query-description"
              style={{
                flex: '1 1',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '2px',
                minWidth: '1px',
              }}
              onClick={onClickDivHandler}
            >
              <div
                style={{
                  flex: '0 0 auto',
                  display:
                    props.query.adqlQuery === undefined || props.query.adqlQuery === ''
                      ? 'flex'
                      : 'none',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                  padding: '0px 0px 10px 0px',
                }}
              >
                {buildQueryDescription({
                  query: props.query,
                  builtQuery: sBuiltQuery,
                  longFormat: true,
                })}
              </div>
              <div style={{ flex: '0 0 auto', width: '100%' }}>ADQL query:</div>
              <div style={{ flex: '0 0 5px' }} />
              <div
                style={{
                  flex: '0 0 auto',
                  border: sG_DarkMode ? '1pt solid #555' : '1pt solid #999',
                  borderRadius: '6px',
                  width: '100%',
                  padding: '4px 4px 4px 4px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {sBuiltQuery !== undefined ? sBuiltQuery : 'running.....'}
              </div>
              <div style={{ flex: '1 1' }}></div>
            </div>
            <div style={{ flex: '0 0 5px' }} />
          </div>
        </Scrollbox>
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 40px',
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            padding: '0px 5px 0px 5px',
          }}
        >
          <ToolButton
            key={t('Save query')}
            name="save-query-button"
            onClick={onClickBtnHandler}
            text={t('Save query')}
            type={ToolButtonType.SECONDARY}
          />
          <div style={{ flex: '0 0 5px' }} />
          <ToolButton
            key={t('ADQL editor')}
            name="adql-editor"
            onClick={onClickBtnHandler}
            text={t('ADQL editor')}
            type={ToolButtonType.SECONDARY}
          />
        </div>
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 16px',
            display: 'flex',
            flexDirection: 'row',
            position: 'absolute',
            bottom: '-8px',
            width: '100%',
          }}
        >
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              border:
                '1pt solid ' +
                (sInfoHovered
                  ? sG_DarkMode
                    ? 'white'
                    : 'black'
                  : sG_DarkMode
                    ? '#555'
                    : '#999'),
              flex: '0 0 20px',
              height: '15px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '5pt',
              backgroundColor: '#040034',
              color: 'white',
              fontWeight: '800',
            }}
          >
            ⋀
          </div>
          <div style={{ flex: '1 1' }} />
        </div>
      </div>
      <div style={{ flex: '0 0 8px', display: sInfoExpanded ? 'flex' : 'none' }} />

      {/* Data product type filter dropdown */}
      <div
        className="search-results-filter-bar"
        style={{
          padding: '10px',
          borderBottom: '1px solid #ccc',
          display: sInfoExpanded ? 'none' : 'block',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0px' }}>
          <MiniButton
            id="save-query-button"
            data-selected="F"
            onClick={onClickBtnHandler}
          >
            <img
              src={saveIcon}
              role="decoration"
              alt=""
              width="28"
              height="28"
              title={t('Save query to project')}
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="refresh-query-button"
            data-selected="F"
            onClick={onClickBtnHandler}
          >
            <img
              src={refreshIcon}
              role="decoration"
              alt=""
              width="28"
              height="28"
              title={t('Refresh query')}
            />
          </MiniButton>
          <div style={{ flex: '0 0 15px' }} />
          <label id="search-catalog-filter-by-type-label" className="header-text">
            {t('Filter results by type:')}
          </label>
          <div style={{ flex: '0 0 15px' }} />
          <select
            aria-labelledby="search-catalog-filter-by-type-label"
            value={sDPTFilter ?? '<all>'}
            onChange={(e) =>
              dispatch(setDPTFilter({ id: props.tabID, dptFilter: e.target.value }))
            }
            className="listbox"
            style={{ minWidth: '150px', maxWidth: '400px' }}
            disabled={sDPTInResults?.length === 1}
            data-disabled={sDPTInResults?.length === 1 ? 'T' : 'F'}
          >
            {sDPTInResults?.map((dpt, index) => (
              <option key={index} value={dpt}>
                {(dpt === '<all>' ? 'All' : dpt !== '' ? dpt : '<none>') +
                  ' [' +
                  (
                    sTotalAllDPTs?.find((item) => item.dpt === dpt)?.total ?? 0
                  ).toString() +
                  ' result(s)]'}
              </option>
            ))}
          </select>
          <div style={{ flex: '1 1' }} />
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <label id="search-catalog-results-per-page-label" className="header-text">
              {t('Results per page:')}
            </label>
            <select
              area-labelledby="search-catalog-results-per-page-label"
              name="searchResultsPerPage"
              onChange={pageSizeOnChangeHandler}
              value={sPageSize}
              className="listbox"
              style={{ minWidth: '80px' }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* the results component contains results for each data product type. all but one of these result sets will
       // be hidden */}
      <div className="search-results-table-holder-visible">
        <SearchResultsTable
          key={
            'dpt_' +
            (sDPTFilter ?? '') +
            '_' +
            (sPage?.toString() ?? '0') +
            '_' +
            (sPageSize ?? 10).toString()
          }
          tabID={props.tabID}
          launchService={props.launchService}
        />
      </div>

      {/* the search-results footer contains the navigation buttons for moving between results pages */}
      <SearchResultsFooter
        key={
          sPages?.toString() +
          '_' +
          (sTotal ?? 0).toString() +
          '_' +
          (sPage?.toString() ?? '0') +
          '_' +
          (sPageSize ?? 10).toString()
        }
        page={sPage ?? 0}
        pageSize={sPageSize ?? 10}
        pages={sPages ?? 0}
        rows={sTotal ?? 0}
        updatePage={updatePage}
        visible={!sInfoExpanded}
      />

      {/*
       //  Show the modal form that allows a user to save a query.
       */}
      <SaveQueryModal
        show={sShowModal}
        handleClose={() => setShowModal(false)}
        positionSearch={
          props.query.ra !== undefined &&
          props.query.dec !== undefined &&
          props.query.fov !== undefined
        }
        query={props.query}
        project={sG_Project}
      />
    </div>
  );
} // SearchCatalogResults

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------
